import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { requestSignupOtp, verifySignupOtp } from "../api/signup";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import {
  getUserAddresses,
  createAddress,
  updateAddress,
} from "../api/deliveryaddress";
import {
  MapPinIcon,
  PhoneIcon,
  User2Icon,
  CheckCircle2Icon,
} from "lucide-react";
import { verifyCoupon } from "../api/coupon";
import { createRazorpayOrder, verifyPayment } from "../api/order";
import gameLogo from "../assests/logo-game.jpeg";
import axios from "axios";
import LoginModal from "../components/LoginModal";
import { seoDefaults } from "../utils/seoDefaults";
import SEO from "../api/seo";
import OrderSuccessModal from "../components/OrderSuccessModal";

const Checkout = () => {
  const { cart, clearCart } = useCart();
  const { login } = useAuth();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [showOtp, setShowOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [otp, setOtp] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponError, setCouponError] = useState("");
  const [placeOrderError, setPlaceOrderError] = useState("");
  const [signupError, setSignupError] = useState("");
  const [orderId, setOrderId] = useState("");
  const [showLogin, setShowLogin] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successOrderId, setSuccessOrderId] = useState("");

  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [addressFormData, setAddressFormData] = useState({
    name: "",
    phone_no: "",
    country: "",
    state: "",
    city: "",
    address: "",
    zip_code: "",
  });
  const [editingAddressId, setEditingAddressId] = useState(null);

  const [addressForm, setAddressForm] = useState({
    name: "",
    phone_no: "",
    country: "",
    state: "",
    city: "",
    address: "",
    zip_code: "",
  });

  const [form, setForm] = useState({
    firstName: "",
    email: "",
    phone_number: "",
    address: "",
    city: "",
    pincode: "",
    country: "",
    state: "",
    notes: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({
    phone_number: "", email: "",});

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser) {
      setUser(storedUser);
      setForm((prev) => ({
        ...prev,
        firstName: storedUser.name || "",
        email: storedUser.email || "",
        phone_number: storedUser.phone_number || "",
        address: storedUser.address || "",
        city: storedUser.city || "",
        pincode: storedUser.pincode || "",
        notes: "",
      }));
    }
  }, []);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser) {
      setUser(storedUser);
      setForm((prev) => ({
        ...prev,
        firstName: storedUser.name || "",
        email: storedUser.email || "",
        phone_number: storedUser.phone_number || "",
      }));
      const token = localStorage.getItem("token");
      getUserAddresses(storedUser._id, token).then((res) => {
        setAddresses(res);
        const defaultAddress = res.find((addr) => addr.isDefault);
      if (defaultAddress) {
        setSelectedAddressId(defaultAddress._id);
      } else if (res.length > 0) {
        setSelectedAddressId(res[0]._id); 
      }
      });
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    // Validation
    if (name === "phone_number") {
    if (value.length !== 10 || !/^\d+$/.test(value)) {
      setErrors((prev) => ({
        ...prev,
        phone_number: "Phone number must be exactly 10 digits",
      }));
    } else {
      setErrors((prev) => ({ ...prev, phone_number: "" }));
    }
  }

    if (name === "email") {
      if (!/^\S+@\S+\.\S+$/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          email: "Enter a valid email address",
        }));
      } else {
        setErrors((prev) => ({ ...prev, email: "" }));
      }
    }
  };

  const subtotal = cart.reduce((sum, item) => {
    const price = item.price || item.product_id?.price || 0;
    const quantity = item.pieces || item.quantity || 1;
    return sum + price * quantity;
  }, 0);

  const discount = couponApplied ? Math.round(subtotal * 0.1) : 0;
  const total = Math.round(subtotal - discount);

  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      const message = "Please enter a coupon code";
      setCouponError(message);
      toast.error(message);
      return;
    }

    if (!user || !user._id) {
      const message = "Please login to apply coupon";
      setCouponError(message);
      toast.error(message);
      return;
    }
    try {
      const data = await verifyCoupon(
        couponCode.trim(),
        user._id,
        orderId || user._id
      );

      if (data && data.data) {
        const couponData = data.data;
        setCouponDiscount(couponData.discount_percentage);
        setCouponApplied(true);
        setCouponError("");
        toast.success(`Coupon Applied: ${couponData.discount_percentage}% off`);
      } else {
        setCouponError(data.message || "Invalid coupon");
        setCouponApplied(false);
        setCouponDiscount(0);
        toast.error(data.message || "Invalid coupon");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Error validating coupon";
      setCouponError(errorMessage);
      setCouponApplied(false);
      setCouponDiscount(0);
      toast.error(errorMessage);
    }
  };

  const handleSignupSubmit = async (e) => {
    const ipInfo = await fetch("https://ipapi.co/json/").then((res) =>
      res.json()
    );
    e.preventDefault();
    setSignupError("");
    const { firstName, email, phone_number, password, confirmPassword } = form;

    if (!firstName || !email || !phone_number) {
      const errorMsg = "Please fill all required fields.";
      setSignupError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    if (!user && (!password || password !== confirmPassword)) {
      const errorMsg = "Passwords do not match.";
      setSignupError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    try {
      setLoading(true);
      await requestSignupOtp({
        name: `${firstName}`,
        email,
        phone_number,
        password,
        ip: ipInfo.ip,
        ip_location: `${ipInfo.city}, ${ipInfo.region}`,
        timezone: ipInfo.timezone,
      });
      toast.success("OTP sent to email");
      setShowOtpModal(true);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Signup failed");
      setSignupError(err?.response?.data?.message || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAddress = async (user, token) => {
    const deliveryAddress =
      addresses.find((addr) => addr._id === selectedAddressId) || form;

    const fullAddress = {
      name: addressForm.name || form.firstName,
      phone_no: addressForm.phone_no || form.phone_number,
      country: deliveryAddress.country || "India",
      state: deliveryAddress.state || "Unknown State",
      city: deliveryAddress.city || "Unknown City",
      address: deliveryAddress.address || "Address not provided",
      zip_code: deliveryAddress.zip_code || deliveryAddress.pincode || "000000",
      userId: user._id,
      email: user.email,
    };

    try {
      await createAddress(fullAddress, token);
      toast.success("Address saved");
    } catch (err) {
      console.error("Failed to save address:", err);
      toast.error("Address save failed");
    }
  };

  const openAddressModal = (addressId = null) => {
    setEditingAddressId(addressId);
    if (addressId) {
      const selected = addresses.find((addr) => addr._id === addressId);
      if (selected) {
        setAddressFormData({
          name: selected.name || "",
          phone_no: selected.phone_no || "",
          country: selected.country || "",
          state: selected.state || "",
          city: selected.city || "",
          address: selected.address || "",
          zip_code: selected.zip_code || "",
        });
      }
    } else {
      setAddressFormData({
        name: "",
        phone_no: "",
        country: "",
        state: "",
        city: "",
        address: "",
        zip_code: "",
      });
    }
    setAddressModalOpen(true);
  };

  const saveNewAddress = async () => {
    const token = localStorage.getItem("token");
    if (editingAddressId) {
      await updateAddress(
        editingAddressId,
        { ...addressFormData, userId: user._id },
        token
      );
    } else {
      await createAddress(
        { ...addressFormData, userId: user._id, email: user.email },
        token
      );
    }
    const updated = await getUserAddresses(user._id, token);
    setAddresses(updated);
    setAddressModalOpen(false);
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const res = await verifySignupOtp(form.email, otp);
      const token = res.token;
      const savedUser = res.user;
      await handleSaveAddress(savedUser, token);
      toast.success("Signup successful");
      login(res.token, res.user);
      setShowOtpModal(false);
      window.location.reload();
    } catch (err) {
      toast.error("Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    setPlaceOrderError("");

    if (!form.firstName || !form.email || !form.phone_number) {
      const errorMsg =
        "Please fill all required fields before placing the order.";
      setPlaceOrderError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    if (!user || !user._id) {
      const errorMsg = "Please verify your OTP or login to continue.";
      setPlaceOrderError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    const deliveryAddress =
      addresses.find((addr) => addr._id === selectedAddressId) || form;

    const items = cart.map((item) => {
      const isPopulated =
        typeof item.product_id === "object" && item.product_id !== null;

      const productId = isPopulated
        ? item.product_id._id
        : item.product_id || item._id || "";
      const categoryId = isPopulated
        ? item.product_id.category_id
        : item.category_id || item.product_category || "";
      const productName = isPopulated
        ? item.product_id.name
        : item.product_name || item.name || "";

      return {
        cart_id: item._id,
        product_id: productId,
        category_id: categoryId,
        product_name: productName,
        quantity: item.pieces || item.quantity || 1,
        price: item.price,
        shipping_charge: item.shipping_charge || 0,
        pieces: item.pieces || 1,
      };
    });

    const payload = {
      user_id: user ? user._id : null,
      location: deliveryAddress.address || form.address || "",
      name: deliveryAddress.name || form.firstName || "",
      city: deliveryAddress.city || form.city || "",
      state: deliveryAddress.state || form.state || "Unknown State",
      country: deliveryAddress.country || form.country || "India",
      zip_code:
        deliveryAddress.pincode ||
        deliveryAddress.zip_code ||
        form.pincode ||
        "000000",
      email: form.email || user?.email || "",
      amount: total || 1,
      notes: form.notes || "",
      phone_number:
        deliveryAddress.phone_no ||
        form.phone_number ||
        user?.phone_number ||
        "",
      items: items.length > 0 ? items : [{}],
    };

    let userIdToSend = user ? user._id : generateTemporaryUserId();

    try {
      const { data } = await createRazorpayOrder(
        total,
        userIdToSend,
        orderId || userIdToSend
      );

      const options = {
        key: "rzp_live_FeWTfDEt7rMxPk",
        amount: data.amount,
        currency: data.currency,
        name: "Game Ztudio",
        description: "Order Payment",
        image: gameLogo,
        order_id: data.order_id,
        handler: async function (response) {
          try {
            const verifyRes = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              user_id: userIdToSend,
              coupon_code: couponApplied ? couponCode : null,
              order_data: payload,
            });

            if (verifyRes.data.success) {
              toast.success("Order placed successfully!");
              clearCart();
              // navigate("/my-orders");
              setShowSuccessModal(true);
            } else {
              toast.error("Payment verification failed");
            }
          } catch (err) {
            const errorMessage =
              err?.response?.data?.message ||
              err.message ||
              "Verification failed";
            toast.error(errorMessage);
            setPlaceOrderError(errorMessage);
          }
        },
        prefill: {
          name: form.firstName,
          email: form.email,
          contact: form.phone_number,
        },
        theme: {
          color: "#030b3c",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err.message ||
        "Order initiation failed";
      toast.error(errorMessage);
      setPlaceOrderError(errorMessage);
      console.error("Order initiation error:", err);
    }
  };

  const generateTemporaryUserId = () => {
    const ts = ((new Date().getTime() / 1000) | 0).toString(16);
    return (
      ts +
      "xxxxxxxxxxxxxxxx"
        .replace(/[x]/g, () => ((Math.random() * 16) | 0).toString(16))
        .toLowerCase()
    );
  };

  return (
    <>
      <SEO {...seoDefaults.checkout} />
      <div className="min-h-screen bg-gray-950 text-white">
        <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 mt-20 sm:mt-20">
            {/* Left Section */}
            <div className="lg:col-span-2 space-y-6">
              <div className="max-w-4xl mx-auto p-6 sm:p-8 bg-gray-900 border border-gray-700 rounded-3xl shadow-lg">
                <div className="flex items-center justify-between mb-6 sm:mb-8">
                  <h1 className="text-2xl sm:text-4xl font-extrabold text-white">
                    Checkout
                  </h1>
                  {!user && (
                    <p className="text-xs sm:text-sm text-gray-400">
                      Already have an account?{" "}
                      <button
                        onClick={() => setShowLogin(true)}
                        className="text-cyan-400 font-semibold hover:underline"
                      >
                        Log in
                      </button>
                      <LoginModal
                        isOpen={showLogin}
                        onClose={() => setShowLogin(false)}
                      />
                    </p>
                  )}
                </div>
                {user ? (
                  // 🔹 User is logged in ➔ Only show Delivery Info
                  // <div className="lg:col-span-2">
                  //   <div className="mx-auto p-6 sm:p-8 bg-gray-900 border border-gray-700 rounded-3xl shadow-lg w-full">
                  //     <div className="space-y-6 w-full">
                  //       <h2 className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500 mb-4">
                  //         Delivery Information
                  //       </h2>

                  //       <div className="grid grid-cols-1 md:grid-cols-2 gap-5 w-full">
                  //         {addresses.length > 0 ? (
                  //           addresses.map((addr) => (
                  //             <label
                  //               key={addr._id}
                  //               className={`block w-full h-full p-5 rounded-2xl border cursor-pointer shadow-lg transition-all duration-300 ${
                  //                 selectedAddressId === addr._id
                  //                   ? "border-cyan-500 bg-gray-800 scale-[1.02] shadow-cyan-500/20"
                  //                   : "border-gray-700 bg-gray-900 hover:scale-[1.01] hover:border-cyan-600"
                  //               }`}
                  //             >
                  //               <input
                  //                 type="radio"
                  //                 name="selectedAddress"
                  //                 value={addr._id}
                  //                 checked={selectedAddressId === addr._id}
                  //                 onChange={() => setSelectedAddressId(addr._id)}
                  //                 className="hidden"
                  //               />

                  //               <div className="space-y-4">
                  //                 <div className="flex items-center gap-3 text-cyan-400 font-semibold text-lg break-words">
                  //                   <User2Icon className="w-5 h-5 shrink-0" />
                  //                   <span>{addr.name}</span>
                  //                 </div>

                  //                 <div className="flex items-start gap-3 text-sm text-gray-300 leading-snug break-words">
                  //                   <MapPinIcon className="w-5 h-5 mt-0.5 text-purple-400 shrink-0" />
                  //                   <span>
                  //                     {addr.address}, {addr.city}, {addr.state},{" "}
                  //                     {addr.country} - {addr.zip_code}
                  //                   </span>
                  //                 </div>

                  //                 <div className="flex items-center gap-3 text-sm text-gray-300 break-words">
                  //                   <PhoneIcon className="w-5 h-5 text-green-400 shrink-0" />
                  //                   <span>{addr.phone_no}</span>
                  //                 </div>
                  //               </div>
                  //             </label>
                  //           ))
                  //         ) : (
                  //           <p className="text-gray-400 text-sm">
                  //             No saved addresses found.
                  //           </p>
                  //         )}
                  //       </div>
                  //     </div>
                  //   </div>
                  // </div>
                  <>
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
                        Delivery Information
                      </h2>
                      <button
                        onClick={() => openAddressModal()}
                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-pink-500 to-purple-600 text-white text-sm font-semibold hover:scale-105 transition-all"
                      >
                        + Add Address
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {addresses.map((addr) => (
                        <label
                          key={addr._id}
                          className={`block p-5 rounded-2xl border cursor-pointer shadow-lg transition-all duration-300 ${
                            selectedAddressId === addr._id
                              ? "border-cyan-500 bg-gray-800 scale-[1.02] shadow-cyan-500/20"
                              : "border-gray-700 bg-gray-900 hover:scale-[1.01] hover:border-cyan-600"
                          }`}
                        >
                          <input
                            type="radio"
                            name="selectedAddress"
                            value={addr._id}
                            checked={selectedAddressId === addr._id}
                            onChange={() => setSelectedAddressId(addr._id)}
                            className="hidden"
                          />
                          <div className="space-y-3">
                            <div className="flex items-center gap-3 text-cyan-400 font-semibold text-lg break-words">
                              <User2Icon className="w-5 h-5 shrink-0" />
                              <span>{addr.name}</span>
                            </div>
                            <p className="text-sm text-gray-300">
                              {addr.address}, {addr.city}, {addr.state},{" "}
                              {addr.country} - {addr.zip_code}
                            </p>
                            <p className="text-sm text-gray-300">
                              {addr.phone_no}
                            </p>
                            <div className="flex gap-2 mt-3">
                              <button
                                onClick={() => openAddressModal(addr._id)}
                                className="px-3 py-1 text-xs rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
                              >
                                Edit
                              </button>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </>
                ) : (
                  // 🔹 User is NOT logged in ➔ Show full signup + address form
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <Input
                      label="Full Name"
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      full
                    />
                    <Input
                      label="Phone Number"
                      name="phone_number"
                      value={form.phone_number}
                      onChange={handleChange}
                      error={errors.phone_number}
                    />
                    <Input
                      label="Email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      error={errors.email}
                    />

                    <Input
                      label="Address"
                      name="address"
                      value={form.address}
                      onChange={handleChange}
                      full
                    />
                    <Input
                      label="City"
                      name="city"
                      value={form.city}
                      onChange={handleChange}
                    />
                    <Input
                      label="Pincode"
                      name="pincode"
                      value={form.pincode}
                      onChange={handleChange}
                    />
                    <Input
                      label="State"
                      name="state"
                      value={form.state}
                      onChange={handleChange}
                    />
                    <Input
                      label="Country"
                      name="country"
                      value={form.country}
                      onChange={handleChange}
                    />

                    <div className="md:col-span-2 flex items-center gap-3">
                      <User2Icon className="w-5 h-5 text-cyan-400" />
                      <div className="text-sm sm:text-base text-white font-semibold">
                        Create account (You’ll set your own password)
                      </div>
                    </div>

                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Input
                        label="Password"
                        name="password"
                        type="password"
                        value={form.password}
                        onChange={handleChange}
                      />
                      <Input
                        label="Confirm Password"
                        name="confirmPassword"
                        type="password"
                        value={form.confirmPassword}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="md:col-span-2 flex justify-center">
                      <button
                        type="submit"
                        onClick={handleSignupSubmit}
                        className="w-full sm:w-56 py-3 text-sm sm:text-base rounded-xl bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-purple-600 hover:to-cyan-500 shadow font-semibold transition duration-300"
                      >
                        {loading ? "Processing..." : "Submit & Get OTP"}
                      </button>
                    </div>
                    <div className="md:col-span-2 flex justify-center">
                      {signupError && (
                        <p className="text-sm text-red-400 font-medium text-center">
                          {signupError}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="md:col-span-2 mt-6">
                  <label className="block text-sm sm:text-base font-semibold mb-2">
                    Order Notes
                  </label>
                  <textarea
                    rows="3"
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    placeholder="Any special instructions"
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm sm:text-base"
                  />
                </div>
              </div>
            </div>

            {showOtpModal && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50">
                <div className="bg-gray-900 p-6 rounded-2xl border border-gray-700 w-full max-w-md">
                  <h2 className="text-xl font-bold mb-4 text-center">
                    Verify OTP
                    <p className="font-normal text-sm text-center text-gray-300 mt-1">
                      We’ve sent a verification OTP to your email:{" "}
                      <span className="text-cyan-400 font-semibold break-words">
                        {form.email}
                      </span>
                    </p>
                  </h2>

                  <Input
                    label="OTP Code"
                    name="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                  />
                  <button
                    onClick={handleOtpSubmit}
                    disabled={loading}
                    className="mt-4 w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-500 text-white font-semibold"
                  >
                    {loading ? "Verifying..." : "Verify OTP"}
                  </button>
                  <button
                    onClick={() => setShowOtpModal(false)}
                    className="mt-3 w-full text-sm text-gray-400 hover:text-cyan-400"
                  >
                    Go Back & Edit
                  </button>
                </div>
              </div>
            )}

            {/* Right Section - Order Summary */}
            <aside className="sticky top-8 bg-gray-900 border border-gray-700 rounded-2xl shadow-lg p-4 sm:p-6 h-fit">
              <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6">
                Order Summary
              </h2>

              {cart.length === 0 ? (
                <p className="text-gray-400">Your cart is empty.</p>
              ) : (
                <div className="space-y-4 mb-6">
                  {cart.map((item) => {
                    const product =
                      item.product_details || item.product_id || item;
                    const productName = product.name || item.name || "Product";
                    const productImage =
                      product.image || item.product_image || "";
                    const productPrice = item.price || product.price || 0;
                    const productQty = item.pieces || item.quantity || 1;

                    return (
                      <div
                        key={item._id || product._id || item.product_id?._id}
                        className="flex items-start justify-between"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-gray-800 rounded-lg overflow-hidden flex items-center justify-center">
                            {productImage ? (
                              <img
                                src={`${process.env.REACT_APP_API_IMAGE_URL}${productImage[0]}`}
                                alt={productName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-xl">🎮</span>
                            )}
                          </div>

                          <div>
                            <h3 className="font-medium text-white line-clamp-1">
                              {productName}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-400">
                              Qty: {productQty}
                            </p>
                          </div>
                        </div>

                        <p className="font-medium text-white">
                          ₹{Number(productPrice).toLocaleString()}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mb-4 sm:mb-6">
                <label className="block text-sm font-medium mb-2">
                  Coupon Code
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-l-lg px-4 py-2 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                    placeholder="Enter coupon"
                  />
                  <button
                    onClick={applyCoupon}
                    className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-r-lg text-white font-medium"
                  >
                    Apply
                  </button>
                </div>
                {couponError && (
                  <p className="text-sm text-red-400 font-medium text-center mt-2">
                    {couponError}
                  </p>
                )}
              </div>

              <div className="space-y-3 border-t border-gray-700 pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Subtotal</span>
                  <span>₹{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Discount</span>
                  <span className="text-green-400">
                    -₹{discount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Shipping</span>
                  <span className="text-green-400">FREE</span>
                </div>
                <div className="flex justify-between text-lg font-bold mt-4 pt-4 border-t border-gray-700">
                  <span>Total</span>
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-purple-500">
                    ₹{total.toLocaleString()}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={handlePlaceOrder}
                className="w-full mt-4 bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-semibold py-3 rounded-xl shadow hover:from-cyan-600 hover:to-purple-700 transition duration-300"
              >
                Place Order
              </button>

              {placeOrderError && (
                <p className="mt-2 text-sm text-red-400 font-medium text-center">
                  {placeOrderError}
                </p>
              )}
            </aside>
          </div>
        </main>
      </div>

      {addressModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 z-50">
          <div className="bg-gray-900 p-6 rounded-2xl border border-gray-700 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4 text-center text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
              {editingAddressId ? "Edit Address" : "Add New Address"}
            </h2>

            <form className="space-y-4">
              {Object.entries(addressFormData).map(([key, value]) => (
                <div key={key}>
                  <label className="block text-sm font-semibold mb-1 text-purple-300 uppercase tracking-wide">
                    {key.replace(/_/g, " ")}
                  </label>
                  <input
                    name={key}
                    value={value}
                    onChange={(e) =>
                      setAddressFormData({
                        ...addressFormData,
                        [key]: e.target.value,
                      })
                    }
                    className="w-full p-3 rounded-lg bg-gray-800 border border-purple-700 text-white focus:ring-2 focus:ring-cyan-500 text-sm"
                  />
                </div>
              ))}

              <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setAddressModalOpen(false)}
                  className="w-full sm:w-auto px-5 py-3 rounded-xl border border-gray-600 text-gray-400 hover:text-cyan-400 hover:border-cyan-400 transition text-sm font-semibold"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={saveNewAddress}
                  className="w-full sm:w-auto px-5 py-3 rounded-xl bg-gradient-to-r from-purple-800 to-cyan-500 text-white font-semibold hover:scale-105 transition"
                >
                  Save Address
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <OrderSuccessModal
          orderId={successOrderId}
          onClose={() => {
            setShowSuccessModal(false);
            navigate("/my-orders");
          }}
        />
      )}
    </>
  );
};

const Input = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  error = "",
  full = false,
}) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="block text-base font-semibold mb-2">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={`Enter ${label.toLowerCase()}`}
      className={`w-full bg-gray-800 border ${
        error ? "border-red-500" : "border-gray-700"
      } rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 ${
        error ? "focus:ring-red-500" : "focus:ring-cyan-500"
      }`}
    />
    {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
  </div>
);

export default Checkout;
